import React from "react";
import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import GenericInput from "src/components/input-components/GenericTextInput";

// --- Type Definitions ---
export interface IContactProperties {
  CallHeading: string;
  Phone: string;
  EmailHeading: string;
  Email: string;
  LocationHeading: string;
  Location: string;
  CorporateHeading: string;
  CorporateDescription: string;
  MapUrl: string;
}

export interface IContactWidget {
  type: string; // should be WidgetType.Contact from your enum
  Title: string;
  Header: string;
  Description: string;
  Properties: IContactProperties;
}

// --- Props for the ContactItem component ---
interface ContactItemProps {
  item: IContactWidget;
  error: any;
  onChange: (updatedItem: IContactWidget, field: string) => void;
  onblur: (field: string, index?: number) => void;
}

const ContactItem: React.FC<ContactItemProps> = ({
  item,
  error,
  onChange,
  onblur,
}) => {
  // Ensure top-level fields are strings
  const widgetTitle: string = typeof item.Title === "string" ? item.Title : "";
  const widgetHeader: string = typeof item.Header === "string" ? item.Header : "";
  const widgetDescription: string =
    typeof item.Description === "string" ? item.Description : "";

  // Destructure properties, defaulting to empty strings if missing.
  const properties: IContactProperties = item.Properties || {
    CallHeading: "",
    Phone: "",
    EmailHeading: "",
    Email: "",
    LocationHeading: "",
    Location: "",
    CorporateHeading: "",
    CorporateDescription: "",
    MapUrl: "",
  };

  // Helper: if the parameter is an event, extract its target.value; otherwise, use it directly.
  const getValue = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ): string => (typeof e === "string" ? e : e.target.value);

  // Handlers for top-level fields.
  const handleTitleChange = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    onChange({ ...item, Title: getValue(e) }, "Title");
  };

  const handleHeaderChange = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    onChange({ ...item, Header: getValue(e) }, "Header");
  };

  const handleDescriptionChange = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    onChange({ ...item, Description: getValue(e) }, "Description");
  };

  // Handler for properties. This updates one property at a time.
  const handlePropertyChange = (
    field: keyof IContactProperties,
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    onChange(
      { ...item, Properties: { ...properties, [field]: getValue(e) } },
      "Properties"
    );
  };

  return (
    <Card elevation={2} sx={{ p: 2, mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Contact Widget
        </Typography>

        {/* Top-level fields */}
        <GenericInput
          type="text"
          name="Title"
          title="Widget Title"
          value={widgetTitle}
          error={error?.Title || ""}
          onChange={(e) => handleTitleChange(e)}
          onBlur={() => onblur("Title")}
          disabled={false}
        />
        <GenericInput
          type="text"
          name="Header"
          title="Header"
          value={widgetHeader}
          error={error?.Header || ""}
          onChange={(e) => handleHeaderChange(e)}
          onBlur={() => onblur("Header")}
          disabled={false}
        />
        <GenericInput
          type="text"
          name="Description"
          title="Description"
          value={widgetDescription}
          error={error?.Description || ""}
          onChange={(e) => handleDescriptionChange(e)}
          onBlur={() => onblur("Description")}
          disabled={false}
        />

        {/* Properties Section */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Contact Properties
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <GenericInput
              type="text"
              name="CallHeading"
              title="Call Heading"
              value={properties.CallHeading}
              error={error?.Properties?.CallHeading || ""}
              onChange={(e) => handlePropertyChange("CallHeading", e)}
              onBlur={() => onblur("CallHeading")}
              disabled={false}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <GenericInput
              type="text"
              name="Phone"
              title="Phone"
              value={properties.Phone}
              error={error?.Properties?.Phone || ""}
              onChange={(e) => handlePropertyChange("Phone", e)}
              onBlur={() => onblur("Phone")}
              disabled={false}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <GenericInput
              type="text"
              name="EmailHeading"
              title="Email Heading"
              value={properties.EmailHeading}
              error={error?.Properties?.EmailHeading || ""}
              onChange={(e) => handlePropertyChange("EmailHeading", e)}
              onBlur={() => onblur("EmailHeading")}
              disabled={false}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <GenericInput
              type="text"
              name="Email"
              title="Email"
              value={properties.Email}
              error={error?.Properties?.Email || ""}
              onChange={(e) => handlePropertyChange("Email", e)}
              onBlur={() => onblur("Email")}
              disabled={false}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <GenericInput
              type="text"
              name="LocationHeading"
              title="Location Heading"
              value={properties.LocationHeading}
              error={error?.Properties?.LocationHeading || ""}
              onChange={(e) => handlePropertyChange("LocationHeading", e)}
              onBlur={() => onblur("LocationHeading")}
              disabled={false}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <GenericInput
              type="text"
              name="Location"
              title="Location"
              value={properties.Location}
              error={error?.Properties?.Location || ""}
              onChange={(e) => handlePropertyChange("Location", e)}
              onBlur={() => onblur("Location")}
              disabled={false}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <GenericInput
              type="text"
              name="CorporateHeading"
              title="Corporate Heading"
              value={properties.CorporateHeading}
              error={error?.Properties?.CorporateHeading || ""}
              onChange={(e) => handlePropertyChange("CorporateHeading", e)}
              onBlur={() => onblur("CorporateHeading")}
              disabled={false}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <GenericInput
              type="text"
              name="CorporateDescription"
              title="Corporate Description"
              value={properties.CorporateDescription}
              error={error?.Properties?.CorporateDescription || ""}
              onChange={(e) => handlePropertyChange("CorporateDescription", e)}
              onBlur={() => onblur("CorporateDescription")}
              disabled={false}
            />
          </Grid>
          <Grid item xs={12}>
            <GenericInput
              type="text"
              name="MapUrl"
              title="Map URL"
              value={properties.MapUrl}
              error={error?.Properties?.MapUrl || ""}
              onChange={(e) => handlePropertyChange("MapUrl", e)}
              onBlur={() => onblur("MapUrl")}
              disabled={false}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default ContactItem;
